// src/pages/returnNote/ReturnNoteListPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RotateCcw, Plus, Eye, Calendar, Clock, CheckCircle, XCircle, ShoppingBag, UserCheck, Banknote, Receipt, Star } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import { getReturnNotes } from '../../services/api/offlineSales/returnNotes';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/orderCalculations';
import DocNumberCell from '../procurement/components/DocNumberCell';
import tb from '../procurement/components/ProcurementTable.module.css';
import styles from './ReturnNoteListPage.module.css';

const STATUS_ICONS = { completed: CheckCircle, voided: XCircle };
const REFUND_METHOD_ICONS = { cash: Banknote, credit_note: Receipt, loyalty_points: Star };

const statusConfig = {
  completed: { color: '#10b981', bg: '#d1fae5' },
  voided: { color: '#ef4444', bg: '#fee2e2' },
};

const ReturnNoteListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [returnNotes, setReturnNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Set when the backend list endpoint is missing (see the KNOWN GAP note in
  // src/services/api/offlineSales/returnNotes.js) so the empty state can explain why, instead of
  // silently looking like "no return notes exist yet".
  const [listUnavailable, setListUnavailable] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100],
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchReturnNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize]);

  const fetchReturnNotes = async () => {
    try {
      setIsLoading(true);
      setListUnavailable(false);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
      };
      const response = await getReturnNotes(params);
      setReturnNotes(response.returnNotes || response.returns || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      console.error('Failed to fetch return notes:', err);
      if (err.response?.status === 404) {
        // Backend has no GET / route for this resource yet (known gap, see returnNotes.js).
        setListUnavailable(true);
        setReturnNotes([]);
      } else if (err.response?.status === 403) {
        showError(t('errors.permissionDenied'));
      } else {
        showError(t('returnNote.failedToLoadReturnNotes'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  const columns = [
    {
      key: 'returnNumber',
      header: t('returnNote.returnNoteNumber'),
      width: '180px',
      render: (row) => <DocNumberCell number={row.returnNumber} to={`/offline-sales/return-notes/${row.id}`} />,
    },
    {
      key: 'originalOrder',
      header: t('returnNote.originalOrder'),
      width: '160px',
      render: (row) => row.originalOrder?.orderNumber ? (
        <Link className={tb.docLink} to={`/offline-sales/orders/${row.originalOrder.id}`} onClick={(e) => e.stopPropagation()}>
          <ShoppingBag size={12} />{row.originalOrder.orderNumber}
        </Link>
      ) : <span className={tb.naText}>—</span>,
    },
    {
      key: 'cashier',
      header: t('returnNote.cashier'),
      render: (row) => row.cashier?.name
        ? <div className={tb.createdByInfo}><UserCheck size={12} />{row.cashier.name}</div>
        : <span className={tb.naText}>—</span>,
    },
    {
      key: 'refundMethod',
      header: t('returnNote.refundMethod'),
      width: '150px',
      render: (row) => {
        const MethodIcon = REFUND_METHOD_ICONS[row.refundMethod] || Receipt;
        return <div className={tb.shopBadge}><MethodIcon size={12} />{t(`returnNote.refundMethod_${row.refundMethod}`)}</div>;
      },
    },
    {
      key: 'totalRefundAmount',
      header: t('returnNote.totalRefundAmount'),
      width: '140px',
      align: 'right',
      render: (row) => <div className={tb.totalAmount}>{formatCurrency(row.totalRefundAmount)}</div>,
    },
    {
      key: 'status',
      header: t('common.status'),
      width: '130px',
      render: (row) => {
        const cfg = statusConfig[row.status] || statusConfig.completed;
        const StatusIcon = STATUS_ICONS[row.status] || Clock;
        return (
          <div className={tb.statusBadge} style={{ backgroundColor: cfg.bg, borderColor: cfg.color, color: cfg.color }}>
            <StatusIcon size={14} />
            <span>{t(`returnNote.status_${row.status}`)}</span>
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      header: t('common.created'),
      width: '160px',
      render: (row) => (
        <div className={tb.dateCell}>
          <div className={tb.dateInfo}><Calendar size={12} />{formatDate(row.createdAt)}</div>
          <div className={tb.timeInfo}>{formatTime(row.createdAt)}</div>
        </div>
      ),
    },
  ];

  const tableActions = {
    render: (row) => (
      <div className={tb.actionsWrapper}>
        <Link
          to={`/offline-sales/return-notes/${row.id}`}
          className={`${tb.actionButton} ${tb.viewButton}`}
          title={t('common.viewDetails')}
        >
          <Eye size={14} />
        </Link>
      </div>
    ),
    width: '90px',
    frozen: true,
  };

  return (
    <div className={styles.returnNoteListPage}>
      <PageHeader
        title={t('returnNote.allReturnNotes')}
        description={t('returnNote.viewAndManageAllReturnNotes')}
        icon={RotateCcw}
        actions={
          hasPermission('create_return_note') && (
            <Link to="/offline-sales/return-notes/create" className={styles.primaryButton}>
              <Plus size={20} />
              {t('returnNote.processReturn')}
            </Link>
          )
        }
      />

      <FilterPanel
        filters={[]}
        values={{}}
        onChange={() => {}}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      {listUnavailable ? (
        <div className={styles.unavailableNotice}>
          <RotateCcw size={40} />
          <h3>{t('returnNote.listUnavailableTitle')}</h3>
          <p>{t('returnNote.listUnavailableDescription')}</p>
          {hasPermission('create_return_note') && (
            <Link to="/offline-sales/return-notes/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('returnNote.processReturn')}
            </Link>
          )}
        </div>
      ) : (
        <DataTable
          data={returnNotes}
          columns={columns}
          actions={tableActions}
          onRowClick={(row) => navigate(`/offline-sales/return-notes/${row.id}`)}
          pagination={pagination}
          onPaginationChange={setPagination}
          loading={isLoading}
          emptyMessage={t('returnNote.noReturnNotesYet')}
          emptyDescription={t('returnNote.noReturnNotesYetDescription')}
          emptyIcon={<RotateCcw size={64} />}
          emptyAction={
            hasPermission('create_return_note') ? (
              <Link to="/offline-sales/return-notes/create" className={styles.primaryButton}>
                <Plus size={18} />
                {t('returnNote.processFirstReturn')}
              </Link>
            ) : null
          }
        />
      )}
    </div>
  );
};

export default ReturnNoteListPage;
